import { CSSProperties, FC, PropsWithChildren, useRef, useState } from 'react';
import debounce from 'lodash.debounce';
import { useSwipeable } from 'react-swipeable';
import { Transition } from 'react-transition-group';
import * as Styles from './bottom-drawer.module.less';
import classNames from 'classnames';

interface BottomDrawerProps {
    open: boolean;
    onClose: () => void;
    duration?: number;
    unmountOnExit?: boolean;
    mountOnEnter?: boolean;
    className?: string;
    backdropClassname?: string;
    fullHeight?: boolean;
    longer?: boolean;
}

export const BottomDrawer: FC<PropsWithChildren<BottomDrawerProps>> = ({
    open,
    onClose,
    duration = 250,
    unmountOnExit = true,
    mountOnEnter = true,
    children,
    className,
    fullHeight,
    longer,
}) => {
    const nodeRef = useRef(null);

    const [currentDeltaY, setDeltaY] = useState(0);
    const swipeHandlers = useSwipeable({
        onSwipedDown: debounce(
            ({ velocity }) => {
                setDeltaY(0);
                if (velocity > 0.5) {
                    onClose();
                }
            },
            500,
            { leading: true }
        ),
        onSwiping: ({ deltaY }) => {
            setDeltaY(deltaY);
        },
    });

    const getTransforms = (): CSSProperties | undefined => {
        if (currentDeltaY <= 0) {
            return undefined;
        }

        return {
            transform: `translate3d(0, ${currentDeltaY}px, 0)`,
            transition: 'none',
        };
    };

    return (
        <Transition
            appear
            in={open}
            timeout={{ appear: 0, enter: 0, exit: duration }}
            unmountOnExit={unmountOnExit}
            mountOnEnter={mountOnEnter}
            nodeRef={nodeRef}
        >
            {state => (
                <div ref={nodeRef} className={className}>
                    <div
                        style={{
                            transition: `opacity ${duration}ms`,
                        }}
                        className={classNames(Styles.bottomDrawerBackdrop, {
                            [Styles.bottomDrawerBackdropEntered]: state === 'entered',
                            [Styles.bottomDrawerBackdropEntering]: state === 'entering',
                            [Styles.bottomDrawerBackdropExiting]: state === 'exiting',
                            [Styles.bottomDrawerBackdropExited]: state === 'exited',
                        })}
                        onClick={onClose}
                    />
                    <div
                        className={classNames(Styles.bottomDrawer, {
                            [Styles.bottomDrawerEntered]: state === 'entered',
                            [Styles.bottomDrawerEntering]: state === 'entering',
                            [Styles.bottomDrawerExiting]: state === 'exiting',
                            [Styles.bottomDrawerExited]: state === 'exited',
                        })}
                        style={{
                            transition: `transform ${duration}ms`,
                            ...getTransforms(),
                        }}
                    >
                        <div {...swipeHandlers} className={Styles.bottomDrawerHandleWrapper}>
                            <div className={Styles.bottomDrawerHandle} />
                        </div>
                        <div
                            className={classNames(Styles.bottomDrawerContentWrapper, {
                                [Styles.bottomDrawerContentWrapperFullHeight]: fullHeight,
                                [Styles.bottomDrawerContentWrapperLonger]: longer,
                            })}
                        >
                            {children}
                        </div>
                    </div>
                </div>
            )}
        </Transition>
    );
};
